/**
 * Organisation API enhancements.
 *
 * Uses `api.enhanceEndpoints()` to add RTK Query cache tags to the
 * auto-generated endpoints without modifying the generated `engineApi.ts`.
 *
 * Also injects hand-written endpoints that are not yet in the generated schema
 * (e.g. the org capabilities endpoint added after the last codegen run).
 *
 * Re-exports typed hooks so callers import from `@proaxiv/server-api` and
 * never reference the raw generated hooks directly.
 *
 * Note: `@rtk-query/codegen-openapi` generates numbered suffixes when the
 * OpenAPI schema has non-unique operationIds. The re-exports below map the
 * generated names back to descriptive aliases to keep the public API stable.
 */
import { api } from './baseApi'

// ── Org-scoped capabilities (hand-written — not yet in generated schema) ─────

/** Shape returned by GET /api/v1/organizations/{orgId}/capabilities */
export interface OrgCapabilities {
  canUpdateOrg: boolean
  canDeleteOrg: boolean
  canInviteMembers: boolean
  canSetFeature: boolean
  canRequestPlanUpgrade: boolean
}

/**
 * Applies cache tags to the org-related endpoints and registers the
 * `OrgCapabilities` tag type so it is available to `injectEndpoints` below.
 *
 * `enhanceEndpoints()` must run before `injectEndpoints` so that the returned
 * enhanced API carries `OrgCapabilities` in its tag-type set, allowing
 * TypeScript to accept `providesTags: ['OrgCapabilities']` on the injected
 * query without a type error.
 */
const enhancedApi = api.enhanceEndpoints({
  addTagTypes: ['OrgList', 'OrgDetail', 'OrgMembers', 'OrgStats', 'OrgCapabilities'],
  endpoints: {
    // ── Org list / CRUD ──────────────────────────────────────────────────
    listOrgs: { providesTags: ['OrgList'] },
    createOrg: { invalidatesTags: ['OrgList'] },
    getOrg: { providesTags: ['OrgDetail'] },
    updateOrg: { invalidatesTags: ['OrgDetail', 'OrgList', 'OrgCapabilities'] },
    deleteOrg: { invalidatesTags: ['OrgList', 'OrgDetail', 'OrgCapabilities'] },
    setOrgStatus: { invalidatesTags: ['OrgList', 'OrgDetail', 'OrgCapabilities'] },
    uploadLogo: { invalidatesTags: ['OrgDetail', 'OrgList'] },
    deleteOrgLogo: { invalidatesTags: ['OrgDetail', 'OrgList'] },

    // ── Join ─────────────────────────────────────────────────────────────
    join: { invalidatesTags: ['OrgList'] },

    // ── Members ──────────────────────────────────────────────────────────
    listMembers: { providesTags: ['OrgMembers'] },
    inviteOrgMember: { invalidatesTags: ['OrgMembers'] },
    resendOrgMemberInvitation: { invalidatesTags: ['OrgMembers'] },
    removeMember: { invalidatesTags: ['OrgMembers'] },

    // ── Stats ────────────────────────────────────────────────────────────
    getOrgStats: { providesTags: ['OrgStats'] },

    // ── Admin list ───────────────────────────────────────────────────────
    listOrganizations: { providesTags: ['OrgList'] },
  },
})

// Inject the org capabilities endpoint on the enhanced API so `OrgCapabilities`
// is already a known tag type and `providesTags` type-checks correctly.
const orgCapabilitiesApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    getOrgCapabilities: build.query<OrgCapabilities, { orgId: string }>({
      query: ({ orgId }) => ({ url: `/api/v1/organizations/${orgId}/capabilities` }),
      providesTags: ['OrgCapabilities'],
    }),
  }),
  overrideExisting: false,
})

export const { useGetOrgCapabilitiesQuery } = orgCapabilitiesApi

// Hooks come from the generated file; re-exported here under stable descriptive
// names so consumers are insulated from codegen numbering changes.
export {
  useListOrgsQuery,
  useCreateOrgMutation,
  useGetOrgQuery,
  useUpdateOrgMutation,
  useDeleteOrgMutation,
  useSetOrgStatusMutation,
  useListMembersQuery,
  useInviteOrgMemberMutation,
  useResendOrgMemberInvitationMutation,
  useRemoveMemberMutation,
  useGetOrgStatsQuery,
  useListOrganizationsQuery,
  useUploadLogoMutation as useUploadOrgLogoMutation,
  useDeleteOrgLogoMutation,
} from './engineApi'
